import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IDocumentEditLock } from '../models/document-edit-lock.model';
import { DocumentEditLockApiService } from '../services/document-edit-lock-api.service';
import {
  AcquireDocumentEditLock,
  ClearDocumentEditLock,
  HeartbeatDocumentEditLock,
  LoadDocumentEditLock,
  ReleaseDocumentEditLock,
  TryAcquireDocumentEditLock,
} from './document-edit-lock.actions';

export interface DocumentEditLockStateModel {
  lock?: IDocumentEditLock;
}

const DOCUMENT_EDIT_LOCK_STATE_TOKEN = new StateToken<DocumentEditLockStateModel>(
  'documentEditLock'
);

@State<DocumentEditLockStateModel>({
  name: DOCUMENT_EDIT_LOCK_STATE_TOKEN,
  defaults: {
    lock: undefined,
  },
})
@Injectable()
export class DocumentEditLockState {
  constructor(private api: DocumentEditLockApiService) {}

  @Action(LoadDocumentEditLock, { cancelUncompleted: true })
  loadDocumentEditLock(
    { setState }: StateContext<DocumentEditLockStateModel>,
    { documentId }: LoadDocumentEditLock
  ) {
    return this.api.getLock(documentId).pipe(
      tap((lock: IDocumentEditLock | null) => {
        setState({ lock: lock ?? undefined });
      })
    );
  }

  @Action(AcquireDocumentEditLock, { cancelUncompleted: true })
  acquireDocumentEditLock(
    { setState, dispatch }: StateContext<DocumentEditLockStateModel>,
    { documentId }: AcquireDocumentEditLock
  ) {
    return this.api.acquireLock(documentId).pipe(
      tap((lock: IDocumentEditLock) => {
        setState({ lock });
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.Conflict) {
          return dispatch(new LoadDocumentEditLock(documentId));
        }

        return throwError(error);
      })
    );
  }

  @Action(TryAcquireDocumentEditLock, { cancelUncompleted: true })
  tryAcquireDocumentEditLock(
    ctx: StateContext<DocumentEditLockStateModel>,
    { documentId }: TryAcquireDocumentEditLock
  ): Observable<unknown> {
    if (!documentId) {
      return of(undefined);
    }

    const current = ctx.getState().lock;
    if (current?.documentId === documentId && current.isOwnedByCurrentUser) {
      return of(undefined);
    }

    return ctx.dispatch(new LoadDocumentEditLock(documentId)).pipe(
      switchMap(() => {
        const lock = ctx.getState().lock;
        if (lock && !lock.isOwnedByCurrentUser) {
          return of(undefined);
        }

        return ctx.dispatch(new AcquireDocumentEditLock(documentId));
      })
    );
  }

  @Action(HeartbeatDocumentEditLock, { cancelUncompleted: true })
  heartbeatDocumentEditLock({ getState, setState }: StateContext<DocumentEditLockStateModel>) {
    const documentId = getState().lock?.documentId;
    if (!documentId) {
      return;
    }

    return this.api.heartbeatLock(documentId).pipe(
      tap((lock: IDocumentEditLock) => {
        setState({ lock });
      }),
      catchError(() => {
        setState({ lock: undefined });
        return of(undefined);
      })
    );
  }

  @Action(ReleaseDocumentEditLock, { cancelUncompleted: true })
  releaseDocumentEditLock({ getState, setState }: StateContext<DocumentEditLockStateModel>) {
    const lock = getState().lock;
    const documentId = lock?.documentId;
    const shouldDelete = !!documentId && !!lock?.isOwnedByCurrentUser;
    setState({ lock: undefined });
    if (!shouldDelete) {
      return;
    }

    return this.api.releaseLock(documentId).pipe(catchError(() => of(undefined)));
  }

  @Action(ClearDocumentEditLock)
  clearDocumentEditLock({ setState }: StateContext<DocumentEditLockStateModel>) {
    setState({ lock: undefined });
  }

  @Selector()
  static getLock(state: DocumentEditLockStateModel) {
    return state.lock;
  }

  @Selector()
  static isHeldByCurrentUser(state: DocumentEditLockStateModel) {
    return !!state.lock?.isOwnedByCurrentUser;
  }
}
